import React, { useEffect, useRef, useState } from "react";
import { Globe, Menu, X } from "lucide-react";
import { LANGUAGES, type LandingLangCode } from "./landingContent";
import { useLandingCopy, type HeadlineKey } from "./landingCopy";

/** Section anchors shown in the nav, with the headline key holding their label. */
const NAV_LINKS: { id: string; labelKey: HeadlineKey }[] = [
  { id: "unique_map", labelKey: "nMap" },
  { id: "personal_guidance", labelKey: "nGuide" },
  { id: "plans", labelKey: "nPlans" },
  { id: "faq", labelKey: "nFaq" },
];

interface LandingNavProps {
  scrolled: boolean;
  onJumpToSection: (id: string) => void;
  onLanguageChange: (code: LandingLangCode) => void;
  onLogin: () => void;
  onGetMap: () => void;
}

const LandingNav: React.FC<LandingNavProps> = ({
  scrolled,
  onJumpToSection,
  onLanguageChange,
  onLogin,
  onGetMap,
}) => {
  const { lang, tr, headline } = useLandingCopy();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  // Close the open popovers on outside click or Escape.
  useEffect(() => {
    if (!menuOpen && !langOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!navRef.current?.contains(target)) setMenuOpen(false);
      if (!langRef.current?.contains(target)) setLangOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMenuOpen(false);
      setLangOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen, langOpen]);

  const jump = (event: React.MouseEvent, id: string) => {
    event.preventDefault();
    setMenuOpen(false);
    onJumpToSection(id);
  };

  return (
    <nav className={`al-nav ${scrolled ? "al-nav--scrolled" : ""}`}>
      <div className="al-nav__inner" ref={navRef}>
        <a
          href="#top"
          className="al-logo"
          aria-label="Astria"
          onClick={(event) => jump(event, "top")}
        >
          <span className="al-logo__mark" aria-hidden="true" />
          Astria
        </a>

        <div
          id="al-nav-links"
          className={`al-nav__links ${menuOpen ? "al-nav__links--open" : ""}`}
        >
          {NAV_LINKS.map(({ id, labelKey }) => (
            <a key={id} href={`#${id}`} onClick={(event) => jump(event, id)}>
              {headline(labelKey)}
            </a>
          ))}
          <button
            type="button"
            className="al-nav__links-login"
            onClick={onLogin}
          >
            {tr("Log in")}
          </button>
        </div>

        <div className="al-nav__actions">
          <div className="al-lang" ref={langRef}>
            <button
              type="button"
              className="al-pill-button"
              aria-label="Language"
              aria-haspopup="true"
              aria-expanded={langOpen}
              onClick={() => setLangOpen((open) => !open)}
            >
              <Globe size={14} aria-hidden="true" />
              <span>{lang.split("-")[0].toUpperCase()}</span>
            </button>
            <div
              className={`al-lang__menu ${langOpen ? "al-lang__menu--open" : ""}`}
            >
              {LANGUAGES.map((language) => (
                <button
                  key={language.code}
                  type="button"
                  lang={language.code}
                  className={language.code === lang ? "is-active" : ""}
                  onClick={() => {
                    setLangOpen(false);
                    onLanguageChange(language.code);
                  }}
                >
                  <span>{language.nativeName}</span>
                  <small>{language.englishName}</small>
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            className="al-pill-button al-nav__login"
            onClick={onLogin}
          >
            {tr("Log in")}
          </button>
          <button type="button" className="al-btn al-btn--primary" onClick={onGetMap}>
            {headline("cta")}
          </button>
          <button
            type="button"
            className="al-nav__burger"
            aria-label="Menu"
            aria-controls="al-nav-links"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X size={18} aria-hidden="true" /> : <Menu size={18} aria-hidden="true" />}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default LandingNav;
